<template>
    <div class="al-page">
        <!-- Header -->
        <div class="al-header">
            <div class="al-header-left">
                <div class="al-icon-wrap"><i class="fas fa-clipboard-list"></i></div>
                <h2 class="al-title">Application Logs</h2>
                <span class="al-count" v-if="result.length">{{ result.length }}</span>
            </div>
        </div>

        <!-- Filters -->
        <div class="al-filters">
            <div class="al-filter-group">
                <label class="al-filter-label">Log Type</label>
                <SelectDropdown
                    v-if="logTypes && logTypes.length"
                    :id="`logtype`"
                    :options="logTypes"
                    :multiple="false"
                    field="logtype"
                    :value="logType"
                    @change="handlelogChange"
                />
            </div>
            <div class="al-filter-group">
                <label class="al-filter-label">Sub Type</label>
                <SelectDropdown
                    v-if="logSubTypes && logSubTypes.length"
                    :id="`logsubtype`"
                    :options="logSubTypes"
                    :multiple="false"
                    field="logsubtype"
                    :value="logSubType"
                    @change="handlelogSubChange"
                />
            </div>
            <button class="al-search-btn" @click="fetchLogs" :disabled="loading">
                <i :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-search'"></i>
                {{ loading ? 'Loading...' : 'View Logs' }}
            </button>
        </div>

        <!-- Modal -->
        <CustomModal
            v-if="showLogDetails && logViewData"
            :title="'Log Details'"
            :show="showLogDetails"
            @close="showLogDetails = false"
        >
            <div class="al-modal-body">
                <div class="al-json-container">
                    <json-viewer :copyable="true" :value="logViewData"></json-viewer>
                </div>
                <div class="al-modal-footer">
                    <button class="al-close-btn" @click="closeModal">Close</button>
                </div>
            </div>
        </CustomModal>

        <!-- Results -->
        <div v-if="loading" class="al-loading"><div class="al-spinner"></div><span>Loading logs...</span></div>
        <div v-else-if="searched && !result.length" class="al-empty">
            <div class="al-empty-icon"><i class="fas fa-clipboard-list"></i></div>
            <h3>No logs found</h3>
            <p>Try different filter options</p>
        </div>
        <div v-else-if="!searched" class="al-empty">
            <div class="al-empty-icon"><i class="fas fa-filter"></i></div>
            <h3>Select filters to view logs</h3>
            <p>Choose a log type and click View Logs</p>
        </div>
        <div v-else class="al-grid">
            <div class="al-card" v-for="(item, idx) in result" :key="item.id || idx" :style="{ animationDelay: `${idx * 0.02}s` }">
                <div class="al-card-top">
                    <div class="al-card-icon" :class="alColor(idx)"><i class="fas fa-scroll"></i></div>
                    <div class="al-card-info">
                        <div class="al-card-badges">
                            <span class="al-type-badge">{{ item.type }}</span>
                            <span class="al-subtype-badge" v-if="item.subType">{{ item.subType }}</span>
                        </div>
                        <span class="al-card-performer" v-if="item.actionPerformedBy">
                            <i class="fas fa-user"></i> {{ item.actionPerformedBy }}
                        </span>
                    </div>
                    <span class="al-card-num">#{{ idx + 1 }}</span>
                </div>
                <div class="al-card-meta">
                    <div class="al-meta-item" v-if="item.timestampFormatted">
                        <i class="fas fa-clock"></i> {{ item.timestampFormatted }}
                    </div>
                    <div class="al-meta-info" v-if="item.info">{{ item.info }}</div>
                </div>
                <div class="al-card-actions">
                    <button class="al-action-btn al-btn-view" @click="handleViewLog(item)"><i class="fas fa-code"></i> View Details</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SelectDropdown from "../../components/crud/SelectDropdown.vue"
import CustomModal from "../../components/layout/CustomModal.vue"
import { http } from "../../utils/http"
import JsonViewer from "vue-json-viewer"
import "vue-json-viewer/style.css"

export default {
    components: { SelectDropdown, CustomModal, JsonViewer },
    data() {
        return {
            logType: "",
            logSubType: "",
            logTypes: [],
            logSubTypes: [],
            result: [],
            searched: false,
            loading: false,
            showLogDetails: false,
            logViewData: null,
        }
    },
    mounted() { this.fetchLogTypes() },
    methods: {
        alColor(i) { return ["al-clr-blue","al-clr-purple","al-clr-teal","al-clr-amber","al-clr-rose"][i % 5] },
        handleViewLog(item) {
            this.showLogDetails = true
            try { this.logViewData = { ...item, jsonData: JSON.parse(item.jsonData) } }
            catch(e) { this.logViewData = { ...item } }
        },
        closeModal() { this.showLogDetails = false; this.logViewData = null },
        handlelogChange(e) { this.logType = e },
        handlelogSubChange(e) { this.logSubType = e },
        async fetchLogTypes() {
            try {
                const res = await http.get("/app-log-types")
                this.logTypes = res.data.result.APP_LOG_TYPES
                this.logSubTypes = res.data.result.APP_LOG_SUB_TYPES
            } catch (err) { console.log("error in fetching log types", err) }
        },
        async fetchLogs() {
            if (this.loading) return
            this.searched = true
            try {
                this.loading = true
                const res = await http.get(`/app-logs?log_type=${this.logType}&log_sub_type=${this.logSubType}`)
                this.result = res.data.result || []
            } catch (err) { console.log("error in fetching logs", err) }
            this.loading = false
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";
.al-page { animation: alFade .3s ease; }
@keyframes alFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.al-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap; }
.al-header-left { display:flex; align-items:center; gap:10px; }
.al-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.al-title { font-size:15px; font-weight:700; color:$gray800; margin:0; }
.al-count { min-width:20px; height:20px; padding:0 6px; border-radius:$radiusFull; font-size:11px; font-weight:700; background:$primaryColor; color:white; display:inline-flex; align-items:center; justify-content:center; }
.al-filters { display:flex; align-items:flex-end; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:14px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap; }
.al-filter-group { flex:1; min-width:180px; }
.al-filter-label { display:block; font-size:11px; font-weight:600; color:$gray500; margin-bottom:5px; text-transform:uppercase; letter-spacing:0.3px; }
.al-search-btn { display:inline-flex; align-items:center; gap:6px; padding:8px 18px; border:none; border-radius:6px; background:$primaryColor; color:white; font-size:12.5px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; white-space:nowrap; align-self:flex-end; margin-bottom:1px; &:hover { background:$primaryDark; } &:disabled { opacity:0.7; cursor:not-allowed; } }
.al-loading { display:flex; align-items:center; justify-content:center; gap:10px; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; span { font-size:13px; color:$gray400; } }
.al-spinner { width:24px; height:24px; border:2.5px solid $gray200; border-top-color:$primaryColor; border-radius:50%; animation:alSpin .7s linear infinite; }
@keyframes alSpin { to { transform:rotate(360deg); } }
.al-empty { text-align:center; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; h3 { font-size:14px; font-weight:700; color:$gray600; margin:0 0 4px; } p { font-size:12px; color:$gray400; margin:0; } }
.al-empty-icon { width:48px; height:48px; border-radius:$radiusFull; background:$gray50; border:1px solid $gray100; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; i { font-size:20px; color:$gray300; } }
.al-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
.al-card { background:white; border:1px solid $gray100; border-radius:10px; padding:14px; box-shadow:$shadowSm; display:flex; flex-direction:column; gap:10px; animation:alSlide .25s ease both; transition:all $transitionFast; &:hover { border-color:$gray200; box-shadow:$shadowMd; transform:translateY(-1px); } }
@keyframes alSlide { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.al-card-top { display:flex; align-items:flex-start; gap:10px; }
.al-card-icon { width:36px; height:36px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
.al-clr-blue { background:#eff6ff; color:#2563eb; }
.al-clr-purple { background:#f5f3ff; color:#7c3aed; }
.al-clr-teal { background:#f0fdfa; color:#0d9488; }
.al-clr-amber { background:#fffbeb; color:#d97706; }
.al-clr-rose { background:#fff1f2; color:#e11d48; }
.al-card-info { flex:1; min-width:0; }
.al-card-badges { display:flex; flex-wrap:wrap; gap:4px; margin-bottom:4px; }
.al-type-badge { font-size:10px; font-weight:700; padding:2px 7px; border-radius:$radiusFull; background:$primaryLight; color:$primaryColor; }
.al-subtype-badge { font-size:10px; font-weight:700; padding:2px 7px; border-radius:$radiusFull; background:#f5f3ff; color:#7c3aed; }
.al-card-performer { font-size:11px; color:$gray500; font-weight:500; display:inline-flex; align-items:center; gap:4px; i { font-size:10px; } }
.al-card-num { font-size:10px; font-weight:700; color:$gray300; flex-shrink:0; }
.al-card-meta { display:flex; flex-direction:column; gap:4px; }
.al-meta-item { font-size:11px; color:$gray400; font-weight:500; display:flex; align-items:center; gap:5px; i { font-size:10px; } }
.al-meta-info { font-size:11.5px; color:$gray600; font-weight:500; line-height:1.4; overflow:hidden; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; }
.al-card-actions { display:flex; gap:6px; margin-top:auto; padding-top:6px; border-top:1px solid $gray100; }
.al-action-btn { display:inline-flex; align-items:center; gap:4px; padding:4px 10px; border:1px solid $gray200; border-radius:5px; font-size:11px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; background:white; i { font-size:10px; } }
.al-btn-view { color:$primaryColor; &:hover { background:$primaryLight; border-color:rgba(0,94,137,0.15); } }
.al-modal-body { padding:4px 0; }
.al-json-container { max-height:300px; border:1px solid $gray100; padding:12px; border-radius:8px; overflow:auto; background:$gray50; margin-bottom:12px; }
.al-json-container::-webkit-scrollbar { width:6px; height:6px; }
.al-json-container::-webkit-scrollbar-track { background:$gray50; }
.al-json-container::-webkit-scrollbar-thumb { background:$gray300; border-radius:3px; }
.al-modal-footer { display:flex; justify-content:flex-end; }
.al-close-btn { padding:6px 16px; border:1px solid $gray200; border-radius:6px; font-size:12px; font-weight:600; cursor:pointer; background:white; color:$gray600; transition:all $transitionFast; font-family:inherit; &:hover { background:$gray50; } }
@media(max-width:992px) { .al-grid { grid-template-columns:repeat(2,1fr); } }
@media(max-width:480px) { .al-grid { grid-template-columns:1fr; } .al-filters { flex-direction:column; align-items:stretch; } .al-filter-group { min-width:100%; } }
</style>
